import { useState } from 'react';
import TemplatePicker from '../menu/TemplatePicker';
import MenuEditor from '../menu/MenuEditor';

/**
 * Post-sign-up steps (T4), shared by OnboardingFlow and SubscriptionGate (the
 * real sign-up path). The owner PIN comes from /api/tenants/register's
 * starterStaff.ownerPin, lives only in React state, and is shown once.
 */
export function OwnerPinNotice({ ownerPin, onContinue }) {
    return (
        <div className="space-y-4">
            {ownerPin && (
                <div className="bg-gray-900 border border-amber-500/60 rounded-xl p-4 text-left">
                    <p className="text-amber-300 text-sm font-semibold mb-1">Your manager PIN</p>
                    <p className="text-4xl font-mono font-bold tracking-widest text-white" aria-label={`Your manager PIN is ${ownerPin.split('').join(' ')}`}>
                        {ownerPin}
                    </p>
                    <p className="text-gray-300 text-sm mt-2">
                        Write this down now. It is shown only once, and you use it to sign in on the POS.
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                        Your account also comes with sample staff whose PINs are well known. Replace them in
                        Manager → Staff before you open.
                    </p>
                </div>
            )}
            <button
                type="button"
                onClick={onContinue}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800"
            >
                {ownerPin ? 'I wrote it down. Set up my menu' : 'Set up my menu'}
            </button>
        </div>
    );
}

/** "Set up your menu": template picker, then the editor; onDone finishes or defers. */
export function MenuSetupStep({ onDone }) {
    const [phase, setPhase] = useState('pick');
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-xl font-bold text-gray-900">Set up your menu</h2>
                <button
                    type="button"
                    onClick={onDone}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                    {phase === 'edit' ? 'Finish setup' : 'Do this later'}
                </button>
            </div>
            {phase === 'pick'
                ? <TemplatePicker onApplied={() => setPhase('edit')} onSkip={() => setPhase('edit')} />
                : <MenuEditor />}
        </div>
    );
}

/** Both steps in sequence, full screen, for SubscriptionGate. */
export default function PostSignupSetup({ ownerPin, onDone }) {
    const [step, setStep] = useState('pin');
    return (
        <div className="min-h-full bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
            {step === 'pin' ? (
                <div className="w-full max-w-md bg-gray-800/50 rounded-2xl p-8 border border-gray-700 text-center" role="status" aria-live="polite">
                    <h2 className="text-2xl font-bold text-white mb-2">Welcome to EmberPOS!</h2>
                    <p className="text-gray-400 mb-4">Your account has been created.</p>
                    <OwnerPinNotice ownerPin={ownerPin} onContinue={() => setStep('menu')} />
                </div>
            ) : (
                <div className="w-full max-w-5xl bg-gray-100 rounded-2xl p-4 sm:p-6">
                    <MenuSetupStep onDone={onDone} />
                </div>
            )}
        </div>
    );
}
